import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, Database, Eye, EyeOff, Info, Search, ShieldAlert } from "lucide-react";
import type { AnalysisResult, GraphNode, SupplyBreakdown } from "../../server/types";
import { compactNumber, riskColor, riskGradient, shortAddress } from "../lib/format";

/** Signal sections, in the order a reader should work through them. */
const GROUP_ORDER = [
  { id: "concentration", label: "Concentration" },
  { id: "clustering", label: "Coordination" },
  { id: "distribution", label: "Distribution" },
  { id: "contract", label: "Contract" },
  { id: "market", label: "Market" },
  { id: "coverage", label: "Coverage" },
] as const;

const SUPPLY_SEGMENTS: Array<{ key: keyof SupplyBreakdown; label: string; color: string }> = [
  { key: "wallets", label: "Wallets", color: "#4f7fd6" },
  { key: "pools", label: "Pools", color: "#2dd4bf" },
  { key: "contracts", label: "Contracts", color: "#f59e0b" },
  { key: "exchanges", label: "Exchanges", color: "#8b93a8" },
  { key: "burned", label: "Burned", color: "#5b6478" },
  { key: "unmapped", label: "Unmapped", color: "#2a3350" },
];

/**
 * Where the supply actually sits. The score only counts the wallet slice, so
 * showing the split explains why a token with a 90% pool can still read as Low.
 */
function SupplyBar({ breakdown }: { breakdown: SupplyBreakdown }) {
  const segments = SUPPLY_SEGMENTS.filter((segment) => breakdown[segment.key] > 0.05);
  return (
    <div className="supply-bar">
      <div className="supply-track">
        {segments.map((segment) => (
          <i
            key={segment.key}
            style={{ width: `${Math.max(0.5, breakdown[segment.key])}%`, background: segment.color }}
            title={`${segment.label} ${breakdown[segment.key].toFixed(2)}%`}
          />
        ))}
      </div>
      <div className="supply-key">
        {segments.map((segment) => (
          <span key={segment.key}>
            <i style={{ background: segment.color }} />
            {segment.label} {breakdown[segment.key].toFixed(1)}%
          </span>
        ))}
      </div>
    </div>
  );
}

interface SidePanelProps {
  data: AnalysisResult;
  selected: GraphNode | null;
  hiddenClusters: Set<string>;
  onToggleCluster: (cluster: string) => void;
  onSelect: (node: GraphNode) => void;
}

type Tab = "holders" | "signals" | "sources";

export function SidePanel({ data, selected, hiddenClusters, onToggleCluster, onSelect }: SidePanelProps) {
  const [tab, setTab] = useState<Tab>("holders");
  const [filter, setFilter] = useState("");
  const filtered = useMemo(() => {
    const query = filter.toLowerCase();
    return data.nodes.filter((node) =>
      !query || node.address.toLowerCase().includes(query) || node.label?.toLowerCase().includes(query),
    );
  }, [data.nodes, filter]);

  return (
    <aside className="side-panel">
      <div className="panel-heading">
        <div><span className="panel-kicker">Holder intelligence</span><h2>Address list</h2></div>
        <span className="sample-badge">{data.stats.mappedNodes} mapped</span>
      </div>
      <div className="panel-tabs">
        <button className={tab === "holders" ? "active" : ""} onClick={() => setTab("holders")}>Holders</button>
        <button className={tab === "signals" ? "active" : ""} onClick={() => setTab("signals")}>Signals <span>{data.signals.length}</span></button>
        <button className={tab === "sources" ? "active" : ""} onClick={() => setTab("sources")}>Coverage</button>
      </div>

      {tab === "holders" && (
        <>
          <label className="holder-search"><Search size={15} /><input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search an address" /></label>
          <div className="cluster-summary">
            {data.clusters.map((cluster, index) => (
              <button key={cluster.id} onClick={() => onToggleCluster(cluster.id)}>
                {hiddenClusters.has(cluster.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                <i style={{ background: cluster.color }} />
                <span>Cluster {index + 1}</span>
                <small>{cluster.members}w · {cluster.links}L · {cluster.percentage.toFixed(2)}%</small>
              </button>
            ))}
            {!data.clusters.length && <p>No connected holder clusters in the sampled history.</p>}
          </div>
          <div className="holder-list">
            {filtered.map((node) => (
              <button key={node.id} className={selected?.id === node.id ? "active" : ""} onClick={() => onSelect(node)}>
                <span className="rank">{node.rank ? `#${node.rank}` : "·"}</span>
                <i className="mini-bubble" style={{ borderColor: node.color, background: node.color }} />
                <span className="holder-id">
                  <strong>{node.label || shortAddress(node.address, 6)}</strong>
                  <small>
                    {node.kind === "wallet" ? compactNumber(node.balance) : node.kind}
                    {node.isHub ? " · hub" : node.origin === "traced" ? " · traced" : ""}
                  </small>
                </span>
                <span className="holder-pct">{node.percentage.toFixed(node.percentage < 1 ? 3 : 2)}%</span>
                <ChevronDown size={14} />
              </button>
            ))}
          </div>
        </>
      )}

      {tab === "signals" && (
        <div className="signal-list">
          <div
            className="score-card"
            style={{ "--score-color": riskColor(data.stats.riskScore) } as React.CSSProperties}
          >
            <span className="score-ring" style={{ "--score": `${data.stats.riskScore * 3.6}deg` } as React.CSSProperties}>
              <strong>{data.stats.riskScore}</strong><small>/100</small>
            </span>
            <span><small>Suspicion score</small><strong>{data.stats.riskLabel}</strong><p>A triage aid, not a safety verdict.</p></span>
          </div>
          <div className="score-scale">
            <i style={{ background: riskGradient() }} />
            <b style={{ left: `${Math.min(100, Math.max(0, data.stats.riskScore))}%` }} />
            <span>Low</span><span>Watch</span><span>Elevated</span><span>High</span>
          </div>

          <SupplyBar breakdown={data.stats.supplyBreakdown} />

          <div className="fact-grid">
            <span><small>Top wallet</small><strong>{data.stats.walletTop1Percentage.toFixed(2)}%</strong></span>
            <span><small>Top 10 wallets</small><strong>{data.stats.walletTop10Percentage.toFixed(1)}%</strong></span>
            <span><small>Largest cluster</small><strong>{data.stats.independentClusterPercentage.toFixed(2)}%</strong></span>
            <span><small>Holders</small><strong>{compactNumber(data.stats.holderCount)}</strong></span>
            <span><small>Market age</small><strong>{data.stats.marketAgeDays === undefined ? "—" : `${Math.round(data.stats.marketAgeDays)}d`}</strong></span>
            <span><small>Context</small><strong>{data.stats.contextFactor.toFixed(2)}x</strong></span>
          </div>

          {GROUP_ORDER.map((group) => {
            const rows = data.signals.filter((signal) => (signal.group || "coverage") === group.id);
            if (!rows.length) return null;
            return (
              <section key={group.id} className="signal-group">
                <h4>{group.label}</h4>
                {rows.map((signal) => {
                  const Icon =
                    signal.severity === "high"
                      ? ShieldAlert
                      : signal.severity === "medium"
                        ? AlertTriangle
                        : signal.severity === "low"
                          ? CheckCircle2
                          : Info;
                  return (
                    <article key={signal.title} className={`signal signal--${signal.severity}`}>
                      <Icon size={18} />
                      <span>
                        <strong>{signal.title}</strong>
                        <p>{signal.detail}</p>
                      </span>
                      {signal.value && <b className="signal-value">{signal.value}</b>}
                    </article>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}

      {tab === "sources" && (
        <div className="coverage-list">
          <div className="coverage-stat-grid">
            <span><strong>{data.stats.mappedNodes}</strong><small>Bubbles</small></span>
            <span><strong>{data.stats.transferConnections}</strong><small>Connections</small></span>
            <span><strong>{data.stats.tracedNodes}</strong><small>Traced out</small></span>
          </div>
          {data.sources.map((source) => (
            <a href={source.url} target="_blank" rel="noreferrer" key={source.name} className="source-row">
              <Database size={17} />
              <span><strong>{source.name}</strong><small>{source.detail}</small></span>
              <i className={`source-status source-status--${source.status}`} />
            </a>
          ))}
          <div className="limitations">
            <h3>Read this map correctly</h3>
            {data.limitations.map((limitation) => <p key={limitation}><Info size={14} />{limitation}</p>)}
          </div>
        </div>
      )}
    </aside>
  );
}
